import React from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Theme } from "../../theme";

// アニメーション
const slideIn = keyframes`
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
`;

// Styled Components - 参照される順に定義
const ArticleDate = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: ${props => props.theme.typography.roles.caption.fontWeight};
	margin-bottom: 0.375rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;

	&::before {
		content: '';
		width: 3px;
		height: 14px;
		background: ${props => props.theme.colors.primary.main};
		border-radius: 2px;
	}
`;

const ArticleTitle = styled.h3<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.primary};
	font-size: ${props => props.theme.typography.roles.title.fontSize};
	font-weight: ${props => props.theme.typography.roles.title.fontWeight};
	margin: 0 0 0.5rem;
	line-height: ${props => props.theme.typography.roles.title.lineHeight};
	transition: color 0.3s ease;

	@media (max-width: 768px) {
		font-size: 1.25rem;
	}
`;

const ArticleDescription = styled.p<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.secondary};
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	line-height: ${props => props.theme.typography.roles.bodySmall.lineHeight};
	margin: 0 0 0.75rem;

	@media (max-width: 768px) {
		font-size: 0.95rem;
	}
`;

const ArticleLink = styled(Link)<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 0.95rem;
	text-decoration: none;
	padding: 0.5rem 1rem;
	border-radius: 0.75rem;
	background: ${props => props.theme.mode === 'light' ? '#f0f4ff' : props.theme.colors.neutral[100]};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		width: 0;
		height: 0;
		border-radius: 50%;
		background: ${props => props.theme.colors.primary.main};
		transform: translate(-50%, -50%);
		transition: width 0.3s ease, height 0.3s ease;
	}

	svg {
		transition: transform 0.2s ease;
		position: relative;
		z-index: 1;
	}

	span {
		position: relative;
		z-index: 1;
	}

	&:hover {
		background: ${props => props.theme.colors.primary.main};
		color: #ffffff;
		border-color: ${props => props.theme.colors.primary.main};
		transform: translateX(4px);

		&::before {
			width: 300px;
			height: 300px;
		}

		svg {
			transform: translateX(4px);
		}
	}
`;

const ArticleCard = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	padding: ${props => props.theme.density.cardPadding};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	animation: ${slideIn} 0.3s ease-out;
	position: relative;
	overflow: hidden;
	box-shadow: ${props => props.theme.shadows.base};

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 4px;
		height: 0;
		background: ${props => props.theme.colors.primary.gradient};
		transition: height 0.2s ease;
	}

	&:hover {
		transform: translateY(-4px);
		box-shadow: ${props => props.theme.shadows.lg};
		border-color: ${props => props.theme.colors.primary.main};

		&::before {
			height: 100%;
		}

		${ArticleTitle} {
			color: ${props => props.theme.colors.primary.main};
		}
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

interface ArticleProps {
	title: string;
	description: string;
	date: string;
	link: string;
}

const Article: React.FC<ArticleProps> = (props) => {
	const { title, description, date, link } = props;
	const { theme } = useTheme();
	const { t } = useLanguage();

	return (
		<ArticleCard theme={theme}>
			<ArticleDate theme={theme}>{date}</ArticleDate>
			<ArticleTitle theme={theme}>{title}</ArticleTitle>
			<ArticleDescription theme={theme}>{description}</ArticleDescription>
			<ArticleLink theme={theme} to={link}>
				<span>{t.outputs.readMore}</span>
				<FontAwesomeIcon icon={faChevronRight} size="sm" />
			</ArticleLink>
		</ArticleCard>
	);
};

export default Article;
